import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, createElementVNode as _createElementVNode } from "vue"

const _hoisted_1 = {
  class: "cm-align-center cm-ui-mt20",
  style: {"flex":"1","justify-content":"flex-end"}
}

import { computed, reactive, ref } from 'vue'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import { ElMessage } from 'element-plus'
import { useI18n } from 'vue-i18n'

import { UpdatePasswordService, RequestParams } from './update-password.service'
import { defaultErrorAction, errorKeyMap } from '@/common/error/error-code'
import { passwordHmac } from '@/utils/format'
import { User, UserInfo } from '@/services/user'
import CmForm from '@/components/cmForm/index.vue'
import { FormActionType, FormSize } from '@/components/cmForm/types'


export default /*#__PURE__*/_defineComponent({
  __name: 'update-password',
  props: {
  modelValue: {
    type: Boolean,
    default: false,
  },
},
  emits: ['submit', 'update:modelValue'],
  setup(__props, { emit: __emit }) {



const emits = __emit

const { t, locale } = useI18n()

const injector = useReflectiveInjector([UpdatePasswordService])
const service = injector.get(UpdatePasswordService)
const user = injector.get(User)

const userInfo = reactive<Partial<UserInfo>>({})
user.userInfo.subscribe(info => { Object.assign(userInfo, info) })

const formRef = ref<InstanceType<typeof CmForm>>()

const formData = reactive({
  original_pwd: '',
  password: '',
  confirm_pwd: '',
})

const passwordRegex = /^(?=.*[0-9])(?=.*[A-Z])(?=.*[a-z])(?=.*[^a-zA-Z0-9]).{8,32}$/

const formColumns = computed(() => {
  void locale.value

  const validatePassword = (_rule: unknown, _value: string, callback: (e?: Error) => void) => {
    // 新密码不能与原始密码相同
    if (formData.password && formData.password === formData.original_pwd) {
      callback(new Error(t('passwordModify.newPasswordSameAsOriginalError')))
      return
    }
    // 校验密码格式
    if (!passwordRegex.test(formData.password)) {
      callback(new Error(t('passwordModify.passwordRuleError')))
      return
    }
    callback()
  }

  const validateConfirmPassword = (_rule: unknown, _value: string, callback: (e?: Error) => void) => {
    if (formData.password !== formData.confirm_pwd) {
      callback(new Error(t('passwordModify.confirmPasswordMismatchError')))
      return
    }
    callback()
  }

  return {
    original_pwd: {
      label: t('passwordModify.labelOriginalPassword'),
      inputType: 'password',
      placeholder: t('passwordModify.originalPasswordPlaceholder'),
      colOpt: { span: 24 },
      inputOpt: { 'show-password': true, clearable: true, autocomplete: 'current-password' },
      rules: [
        { required: true, message: t('passwordModify.originalPasswordRequired'), trigger: 'blur' },
        {
          custom: {
            validator: (_rule: unknown, _value: string, callback: (e?: Error) => void) => {
              if (formData.password && formData.password === formData.original_pwd) {
                callback(new Error(t('passwordModify.newPasswordSameAsOriginalError')))
                return
              }
              callback()
            },
          },
          trigger: ['blur', 'change'],

        },
      ],
    },
    password: {
      label: t('passwordModify.labelNewPassword'),
      inputType: 'password',
      placeholder: t('passwordModify.newPasswordPlaceholder'),
      colOpt: { span: 24 },
      inputOpt: { 'show-password': true, clearable: true, autocomplete: 'new-password' },
      rules: [
        { required: true, message: t('passwordModify.newPasswordRequired'), trigger: 'blur' },
        { custom: { validator: validatePassword }, trigger: ['blur', 'change'] },
      ],
    },
    confirm_pwd: {
      label: t('passwordModify.labelConfirmPassword'),
      inputType: 'password',
      placeholder: t('passwordModify.confirmPasswordPlaceholder'),
      colOpt: { span: 24 },
      inputOpt: { 'show-password': true, clearable: true, autocomplete: 'new-password' },
      rules: [
        { required: true, message: t('passwordModify.confirmPasswordRequired'), trigger: 'blur' },
        { custom: { validator: validateConfirmPassword }, trigger: 'blur' },
      ],
    },
  }
})

async function handleConfirm() {
  if (!await formRef.value?.handleSubmit()) {
    return
  }
  const params: RequestParams = {
    original_pwd: passwordHmac(formData.original_pwd),
    password: passwordHmac(formData.password),
    confirm_pwd: passwordHmac(formData.confirm_pwd),
  }
  service.updatePassword(params).then(() => {
    ElMessage.success(t('passwordModify.modifySuccess'))
    user.updateUserinfo({
      ...(user.getUserInfo() || {}),
      need_update_pwd: false,
    })
    emits('submit')
    handleClose()
  }).catch(err => {
    if (err.responseBody?.ret === errorKeyMap.WRONG_ACCOUNT_PASSWORD.ret) {
      ElMessage.error(t('passwordModify.originalPasswordError'))
    } else {
      defaultErrorAction(err)
    }
  })
}

function handleClose() {
  formRef.value?.handleReset()
  formData.original_pwd = ''
  formData.password = ''
  formData.confirm_pwd = ''
  emits('update:modelValue', false)
}

return (_ctx: any,_cache: any) => {
  const _component_el_alert = _resolveComponent("el-alert")!
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_dialog = _resolveComponent("el-dialog")!

  return (_openBlock(), _createBlock(_component_el_dialog, {
    "append-to-body": "",
    title: _unref(t)('passwordModify.title'),
    "model-value": __props.modelValue,
    width: 550,
    "destroy-on-close": "",
    "show-close": !userInfo.need_update_pwd,
    "close-on-press-escape": !userInfo.need_update_pwd,
    "close-on-click-modal": !userInfo.need_update_pwd,
    onClose: handleClose
  }, {
    default: _withCtx(() => [
      (userInfo.need_update_pwd)
        ? (_openBlock(), _createBlock(_component_el_alert, {
            key: 0,
            class: "cm-ui-mb10",
            style: {"border-radius":"calc(var(--el-border-radius-large) - 3px)"},
            type: "warning",
            "show-icon": "",
            closable: false,
            title: _unref(t)('passwordModify.initialPasswordWarning')
          }, null, 8, ["title"]))
        : _createCommentVNode("", true),
      _createVNode(CmForm, {
        ref_key: "formRef",
        ref: formRef,
        modelValue: formData,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((formData) = $event)),
        formSize: _unref(FormSize).Small,
        formType: _unref(FormActionType).Other,
        "onUpdate:formType": _cache[1] || (_cache[1] = ($event: any) => ((_unref(FormActionType).Other) = $event)),
        formColumns: formColumns.value,
        formOpt: { 'label-width': 'auto', 'label-position': 'top', skipSubmitMsg: true }
      }, null, 8, ["modelValue", "formSize", "formType", "formColumns"]),
      _createElementVNode("div", _hoisted_1, [
        (!userInfo.need_update_pwd)
          ? (_openBlock(), _createBlock(_component_el_button, {
              key: 0,
              size: "small",
              type: "primary",
              plain: "",
              onClick: handleClose
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_unref(t)('common.cancel')), 1)
              ]),
              _: 1
            }))
          : _createCommentVNode("", true),
        _createVNode(_component_el_button, {
          size: "small",
          type: "primary",
          onClick: handleConfirm
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_unref(t)('common.confirm')), 1)
          ]),
          _: 1
        })
      ])
    ]),
    _: 1
  }, 8, ["title", "model-value", "show-close", "close-on-press-escape", "close-on-click-modal"]))
}
}

})